import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PLAYER_STATS_URL = `${API_BASE_URL}/stats/players`;
const CAMPAIGN_DISCOVERY_URL = `${API_BASE_URL}/stats/campaign/discovery`;
const CAMPAIGN_DASHBOARD_URL = `${API_BASE_URL}/stats/campaign/dashboard`;
const CAMPAIGN_BASKET_KILLDT_URL = `${API_BASE_URL}/stats/campaign/basket/killdt`;
const SERVER_TIME_URL = `${API_BASE_URL}/stats/server-time`;

// 서버(우분투 서버)의 System.currentTimeMillis()와 클라이언트 시각의 차이(ms)
let serverTimeOffsetMs = 0;

// 모든 API 응답의 Date 헤더로 매번 오차를 보정 (같은 서버가 내려주는 값이므로 별도 호출 없이도 갱신됨)
axios.interceptors.response.use((response) => {
  const dateHeader = response.headers && response.headers.date;
  if (dateHeader) {
    const serverTime = new Date(dateHeader).getTime();
    if (!isNaN(serverTime)) {
      serverTimeOffsetMs = serverTime - Date.now();
    }
  }
  return response;
});

/**
 * /stats/server-time을 호출해 서버(우분투 머신)의 실제 시각으로 오차를 동기화합니다.
 * @returns {Promise<void>}
 */
export const syncServerTime = async () => {
  try {
    const response = await axios.get(SERVER_TIME_URL);
    if (response.data && response.data.processed && response.data.processed.code === 1) {
      const requestedAt = Date.now();
      serverTimeOffsetMs = response.data.data.serverTime - requestedAt;
    }
  } catch (error) {
    console.error('서버 시각 동기화 중 오류 발생:', error);
  }
};

/**
 * 클라이언트 기기의 시계 오차와 무관하게, 서버 기준 현재 시각을 반환합니다.
 * @returns {Date}
 */
export const getServerNow = () => new Date(Date.now() + serverTimeOffsetMs);

/**
 * API에서 캠페인 목록을 가져옵니다.
 * @returns {Promise<Object>} 캠페인 목록 데이터를 담은 프로미스 객체
 */
export const getCampaignDiscovery = async () => {
  try {
    const response = await axios.get(CAMPAIGN_DISCOVERY_URL);
    if (response.data && response.data.processed && response.data.processed.code === 1) {
      return response.data.data;
    } else {
      const errorMessage = response.data && response.data.processed ? response.data.processed.message : 'API 요청에 실패했습니다.';
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('캠페인 목록을 가져오는 중 오류 발생:', error);
    throw error;
  }
};

/**
 * API에서 캠페인 대시보드 데이터를 가져옵니다。
 * @param {number} campaignKey - 캠페인 키
 * @returns {Promise<Object>} 대시보드 데이터를 담은 프로미스 객체
 */
export const getCampaignDashboard = async (campaignKey) => {
  try {
    const response = await axios.post(CAMPAIGN_DASHBOARD_URL, {
      campaignKey,
    });

    if (response.data && response.data.processed && response.data.processed.code === 1) {
      return response.data.data;
    } else {
      const errorMessage = response.data && response.data.processed ? response.data.processed.message : 'API 요청에 실패했습니다.';
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('캠페인 대시보드 정보를 가져오는 중 오류 발생:', error);
    throw error;
  }
};


/**
 * 랜덤 박터트리기(게임타입 2) 바스켓의 killdt(자동 파괴 시각)를 수정합니다.
 * @param {number} basketkey - 바스켓 키
 * @param {string} killdt - 'YYYY-MM-DD HH:mm:ss' 형식의 날짜/시간 문자열
 * @returns {Promise<Object>} 수정 결과 데이터를 담은 프로미스 객체
 */
export const updateBasketKillDate = async (basketkey, killdt) => {
  try {
    const response = await axios.post(CAMPAIGN_BASKET_KILLDT_URL, {
      basketkey,
      killdt,
    });

    if (response.data && response.data.processed && response.data.processed.code === 1) {
      return response.data.data;
    } else {
      const errorMessage = response.data && response.data.processed ? response.data.processed.message : 'API 요청에 실패했습니다.';
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('바스켓 killdt를 수정하는 중 오류 발생:', error);
    throw error;
  }
};

/**
 * API에서 플레이어 통계를 가져옵니다.
 * @param {Object} params - API 요청 파라미터
 * @param {string} params.campaignFrom - 캠페인 시작일
 * @param {string} params.campaignTo - 캠페인 종료일
 * @param {string} params.dateFrom - 통계 조회 시작일
 * @param {string} params.dateTo - 통계 조회 종료일
 * @returns {Promise<Object>} 통계 데이터를 담은 프로미스 객체
 */
export const getPlayerStats = async ({ campaignFrom, campaignTo, dateFrom, dateTo }) => {
  try {
    const response = await axios.post(PLAYER_STATS_URL, {
      campaignFrom,
      campaignTo,
      dateFrom,
      dateTo,
    });

    if (response.data && response.data.processed && response.data.processed.code === 1) {
      return response.data.data;
    } else {
      const errorMessage = response.data && response.data.processed ? response.data.processed.message : 'API 요청에 실패했습니다.';
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('플레이어 통계 정보를 가져오는 중 오류 발생:', error);
    throw error;
  }
};